import React, { useEffect, useMemo, useState } from "react";
import styles from "./QuickLogin.module.scss";
import ApiService from "../../services/api";
import fireToast from "../../helpers/toaster";
import { useNavigate } from "react-router-dom";
import { PATHS } from "../../constants/paths";
import { ROLES } from "../../constants/roles";
import { setUserCookies } from "../../helpers/cookies";
import { persistUserProfile } from "../../helpers/userProfileStorage";
import { DEV_LOGIN_CREDENTIALS, ENABLE_DEV_LOGIN } from "./loginDevCredentials";

const getPathByRole = (role) => {
  switch (role) {
    case ROLES.RECEPTIONIST:
      return PATHS.RECEPTIONIST_DASHBOARD;
    case ROLES.PRE_OPD_MANAGEMENT:
      return PATHS.PRE_OPD_ASSESSMENTS;
    case ROLES.DOCTOR:
      return PATHS.DOCTOR_DASHBOARD;
    case ROLES.FOLLOW_UP_MANAGEMENT:
      return PATHS.FOLLOW_UP_LIST;
    case ROLES.COUNSELLOR:
      return PATHS.COUNSELLOR_DASHBOARD;
    case ROLES.NURSE:
      return PATHS.NURSE_DASHBOARD;
    case ROLES.IPD_MANAGEMENT:
      return PATHS.IPD_DASHBOARD;
    case ROLES.PHARMACIST:
      return PATHS.PHARMACIST_DASHBOARD;
    case ROLES.RADIOLOGIST:
      return PATHS.RADIOLOGIST_DASHBOARD;
    case ROLES.LAB_TEST:
      return PATHS.LAB_TEST_DASHBOARD;
    case ROLES.THERAPIST:
      return PATHS.THERAPIST_DASHBOARD;
    case ROLES.YOGA:
      return PATHS.YOGA_DASHBOARD;
    case ROLES.BILLING_MANAGER:
      return PATHS.BILLING_MANAGER_DASHBOARD;
    case ROLES.DIETICIAN:
      return PATHS.DIETICIAN_DASHBOARD;
    case ROLES.STAFF_MANAGER:
      return PATHS.STAFF_MANAGER_STAFF;
    case ROLES.STORE_MANAGER:
      return PATHS.STORE_MANAGER_DASHBOARD;
    case ROLES.LOGISTICS:
      return PATHS.LOGISTICS;
    case ROLES.ADMIN:
      return PATHS.ADMIN_DASHBOARD;
    default:
      return null;
  }
};

/** Optional one-line context per role — omit entries for a cleaner list. */
const ROLE_SUBTEXT = {
  "Staff Manager": "Staff directory & onboarding",
};

const QuickLogin = () => {
  const [pendingLabel, setPendingLabel] = useState(null);
  const [query, setQuery] = useState("");
  const navigate = useNavigate();
  const isBusy = pendingLabel !== null;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return DEV_LOGIN_CREDENTIALS;
    return DEV_LOGIN_CREDENTIALS.filter((c) =>
      c.label.toLowerCase().includes(q),
    );
  }, [query]);

  const handleLogin = (cred) => {
    setPendingLabel(cred.label);
    ApiService.login({ email: cred.email, password: cred.password })
      .then((response) => {
        localStorage.setItem("user_token", response.data.token);
        setUserCookies(response.data.user);
        persistUserProfile(response.data.user);
        const loginRoles =
          response.data.user.tokenRoles || response.data.user.role || [];
        if (loginRoles.length > 1) {
          navigate(PATHS.ROLES, { state: { roles: loginRoles } });
        } else {
          const singleRole = loginRoles[0];
          localStorage.setItem("user_role", singleRole);
          const path = getPathByRole(singleRole);
          if (path) navigate(path);
        }
      })
      .catch((error) => {
        if (error.response) {
          fireToast("error", error.response.data.message, "bottom-right", true);
        } else {
          fireToast(
            "error",
            "Could not reach the server. Try again.",
            "bottom-right",
            true,
          );
        }
      })
      .finally(() => setPendingLabel(null));
  };

  useEffect(() => {
    const token = localStorage.getItem("user_token");
    const role = localStorage.getItem("user_role");
    if (token) {
      const path = getPathByRole(role);
      if (path) navigate(path);
    }
  }, [navigate]);

  return (
    <div className={styles.shell}>
      <div className={styles.fx} aria-hidden>
        <div className={styles.fx_grid} />
        <div className={styles.fx_glow} />
      </div>

      <div className={styles.inner}>
        <div className={styles.panel}>
          <header className={styles.header}>
            {ENABLE_DEV_LOGIN && (
              <p className={styles.kicker}>DEV Environment · quick entry</p>
            )}
            <h1 className={styles.title}>Role access</h1>
            <p className={styles.lede}>
              Select a role to continue. New roles only need a line in{" "}
              <code className={styles.code}>loginDevCredentials.js</code>.
            </p>
          </header>

          <div className={styles.filter}>
            <label className={styles.filter_label} htmlFor="quick-login-filter">
              Search by role name
            </label>
            <input
              id="quick-login-filter"
              className={styles.filter_input}
              type="search"
              placeholder="Type a role name…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              disabled={isBusy}
              autoComplete="off"
              spellCheck={false}
            />
          </div>

          <ul className={styles.list}>
            {filtered.length === 0 ? (
              <li className={styles.empty} role="status">
                No match for{" "}
                <span className={styles.empty_q}>{query.trim()}</span>. Clear
                the filter to show all roles.
              </li>
            ) : (
              filtered.map((cred) => {
                const active = pendingLabel === cred.label;
                const credReady =
                  String(cred.email || "").trim() !== "" &&
                  String(cred.password || "").trim() !== "";
                const sub = ROLE_SUBTEXT[cred.label];
                const idx = String(
                  DEV_LOGIN_CREDENTIALS.findIndex(
                    (c) => c.label === cred.label,
                  ) + 1,
                ).padStart(2, "0");

                return (
                  <li key={cred.label} className={styles.list_item}>
                    <button
                      type="button"
                      className={`${styles.row} ${
                        !credReady ? styles.row_pending : ""
                      }`}
                      onClick={() => handleLogin(cred)}
                      disabled={isBusy || !credReady}
                      aria-busy={active}
                      aria-label={
                        credReady
                          ? `Sign in as ${cred.label}`
                          : `${cred.label} — add credentials first`
                      }
                    >
                      <span className={styles.row_index} aria-hidden>
                        {idx}
                      </span>
                      <span className={styles.row_main}>
                        <span className={styles.row_label}>{cred.label}</span>
                        {sub ? (
                          <span className={styles.row_sub}>{sub}</span>
                        ) : !credReady ? (
                          <span className={styles.row_sub}>
                            Configure email and password in credentials file
                          </span>
                        ) : null}
                      </span>
                      <span className={styles.row_signal} aria-hidden>
                        {active ? (
                          <span className={styles.pulse} />
                        ) : (
                          <span className={styles.row_mark} />
                        )}
                      </span>
                    </button>
                  </li>
                );
              })
            )}
          </ul>

          <footer className={styles.footer}>
            <button
              type="button"
              className={styles.back}
              onClick={() => navigate(PATHS.LOGIN)}
              disabled={isBusy}
            >
              ← Standard sign in
            </button>
          </footer>
        </div>
      </div>
    </div>
  );
};

export default QuickLogin;
